import { useEffect, useRef, useState } from 'react'
import { ChevronUpDownIcon, CloseIcon } from '../icons'
import { usePopperSelectDropdown } from '../utils'
import Button from '../Button'
import TextInput from './TextInput'
import clsx from 'clsx'
import './TagInput.css'
import ButtonGroup from '../ButtonGroup'

interface TagInputProps {
    value: string[]
    onChange: (value: string[]) => void
    placeholder?: string
    size?: 'small' | 'regular'
}

const MAX_DISPLAY = 2

export default function TagInput({ value, onChange, placeholder, size = 'regular' }: TagInputProps) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const {
        setReferenceElement,
        setPopperElement,
        attributes,
        styles,
    } = usePopperSelectDropdown()

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => inputRef.current?.focus())
        }
    }, [open])

    const handlePaste = (e: React.ClipboardEvent) => {
        const text = e.clipboardData?.getData('text')
        if (!text) return
        const items = text.split(/[\n\r\t,]+/).map(s => s.trim()).filter(Boolean)
        if (items.length > 1) {
            e.preventDefault()
            const unique = items.filter(item => !value.includes(item))
            if (unique.length) {
                onChange([...value, ...unique.filter((v, i, a) => a.indexOf(v) === i)])
            }
            setQuery('')
        }
    }

    const addCustomValue = () => {
        const trimmed = query.trim()
        if (trimmed && !value.includes(trimmed)) {
            onChange([...value, trimmed])
        }
        setQuery('')
    }

    const removeValue = (item: string) => {
        onChange(value.filter(v => v !== item))
    }

    const displayLabel = () => {
        if (!value.length) return <span className="tag-input-placeholder">{placeholder ?? ''}</span>
        const limit = value[0].length > 20 ? 1 : MAX_DISPLAY
        const shown = value.slice(0, limit).join(', ')
        const remaining = value.length - limit
        return remaining > 0 ? `${shown} + ${remaining} more` : shown
    }

    const showAddOption = query.trim() && !value.includes(query.trim())

    return (
        <div
            ref={containerRef}
            className={clsx('ui-select plain tag-input-select', size)}
        >
            <button
                type="button"
                className={clsx('select-button', size)}
                ref={setReferenceElement}
                onClick={() => setOpen(!open)}
            >
                <span className="select-button-label">{displayLabel()}</span>
                <span className="select-button-icon">
                    <ChevronUpDownIcon aria-hidden="true" />
                </span>
            </button>
            {open && (
                <ul
                    className="tag-input-options select-options nowheel"
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                >
                    <li
                        className="tag-input-search"
                        onKeyDown={e => {
                            if (e.key === 'Enter' && query.trim()) {
                                e.preventDefault()
                                addCustomValue()
                            }
                        }}
                    >
                        <ButtonGroup>
                            <TextInput
                                inputRef={inputRef}
                                size="small"
                                type="text"
                                name="tag-search"
                                placeholder={placeholder}
                                hideLabel
                                value={query}
                                onChange={value => setQuery(value)}
                                onPaste={handlePaste}
                            />
                            {showAddOption
                                ? <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={addCustomValue}
                                >Add</Button>
                                : undefined}
                        </ButtonGroup>
                    </li>
                    {value.map(item => (
                        <li
                            key={item}
                            className="select-option selected tag-input-item"
                            onClick={() => removeValue(item)}
                        >
                            <span>{item}</span>
                            <Button
                                variant="plain"
                                size="tiny"
                                icon={<CloseIcon />}
                                onClick={e => {
                                    e.stopPropagation()
                                    removeValue(item)
                                }}
                            />
                        </li>
                    ))}
                    {!value.length && !showAddOption && (
                        <li className="tag-input-empty">No values added</li>
                    )}
                </ul>
            )}
        </div>
    )
}
